import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  inputBinding,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs';
import {ModalService} from '@shared/services/modal.service';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      '(document:keyup)': 'handleKeyboardEvent($event)'
    }
})
export class ModalComponent {

  private readonly modalContent = viewChild('modalContent', {read: ViewContainerRef});

  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly modalVisible = computed(() => this.modalService.activeModal() !== null);

  constructor() {
    // Effect for imperative side-effect: creating/destroying dynamic component.
    // Reading both activeModal() and modalContent() ensures the effect re-runs
    // when the @if renders the ng-template (viewChild signal updates).
    effect(() => {
      const modal = this.modalService.activeModal();
      const container = this.modalContent();
      if (modal && container) {
        container.clear();
        const bindings = modal.data === undefined
          ? []
          : [inputBinding('modalDataInput', () => modal.data)];
        container.createComponent(modal.component, {bindings});
      } else if (modal === null) {
        container?.clear();
      }
    });

    // Close modal on route navigation
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.modalVisible()) {
        this.closeModal();
      }
    });
  }

  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.modalService.closeModal();
    }
  }

  handleTabKey(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }

    const modalContentEl = (event.currentTarget as HTMLElement);
    const focusableElements = modalContentEl.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstEl) {
      event.preventDefault();
      lastEl.focus();
    } else if (!event.shiftKey && document.activeElement === lastEl) {
      event.preventDefault();
      firstEl.focus();
    }
  }

  public closeModal(): void {
    this.modalService.closeModal();
  }
}
